import * as Colors from '@brightlayer-ui/colors';
import CancelIcon from '@mui/icons-material/Cancel';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GetOrganizationsForAdmin } from '../../../server/client/ableEdge/types';
import { FilledTextField } from '../../components/FilledTextField';
import { Spinner } from '../../components/Spinner';
import { useAlert } from '../../lib/AlertContext';
import { OrganizationService } from '../../services/organization/organization.service';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { Entitlement } from '../../services/subscription/types';

const styles = {
    container: {
        padding: '24px',
        color: Colors.gray[500],
        backgroundColor: Colors.white[50],
        margin: '24px',
        borderRadius: '2px',
    },
    icon: {
        fontSize: 56,
        color: Colors.blue[500],
    },
    spinner: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        backgroundColor: 'rgba(248, 250, 252, 0.7)', // Match Colors.white[50] with opacity
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'wait',
    },
};

export const CreateSubscription: React.FC = () => {
    const { t } = useTranslation();
    const { showInfoAlertToUser } = useAlert();
    const navigate = useNavigate();

    const [entitlement, setEntitlement] = useState<null | undefined | Entitlement>(null);
    const [entitlementError, setEntitlementError] = useState<string | null>(null);
    const [licenseId, setLicenseId] = useState('');
    const [organizations, setOrganizations] = useState<GetOrganizationsForAdmin[]>([]);
    const [selectedOrganization, setSelectedOrganization] = useState('');

    const [isVerifying, setIsVerifying] = useState(false);
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);

    useEffect(() => {
        // Fetch organizations for admin
        const fetchOrganizations = async (): Promise<void> => {
            const [fetchedOrganizations] = await Promise.all([OrganizationService.getOrganizationsForAdmin()]);
            setOrganizations(fetchedOrganizations);
        };

        void fetchOrganizations();
    }, []);

    const verifySubscriptionId = async (): Promise<void> => {
        setIsVerifying(true);
        try {
            const fetchedData = await SubscriptionService.validateEntitlement({ licenseId: licenseId });

            if (!fetchedData) {
                setEntitlement(undefined);
                setEntitlementError('Invalid license ID');
                return;
            }

            setEntitlement(fetchedData);
        } catch (error) {
            setEntitlement(undefined);
            setEntitlementError(error instanceof Error ? error.message : String(error));
        } finally {
            setIsVerifying(false);
        }
    };

    const renderFailedToCreateError = (): void => {
        showInfoAlertToUser({
            icon: (
                <CancelIcon
                    data-testid="create-subscription-error"
                    sx={{ width: 38, height: 38, color: Colors.red[500] }}
                />
            ),
            iconBGColor: Colors.red[50],
            title: t('COMMON.ERROR'),
            description: t('CREATE_SUBSCRIPTION_PAGE.ERROR_CREATING_SUBSCRIPTION'),
            onDismiss: () => {},
        });
    };

    const successInformationMessage = (): void => {
        showInfoAlertToUser({
            title: t('CREATE_SUBSCRIPTION_PAGE.CREATED'),
            description: t('CREATE_SUBSCRIPTION_PAGE.CREATED_DESCRIPTION'),
            icon: <CheckOutlinedIcon sx={{ width: 38, height: 38, color: Colors.green[500] }} />,
            iconBGColor: Colors.green[50],
            onDismiss: () => {
                navigate('/');
            },
        });
    };

    const createSubscription = async (orgId: string, licId: string): Promise<void> => {
        setIsCreatingSubscription(true);
        try {
            await SubscriptionService.createSubscription({
                organizationId: orgId,
                licenseId: licId,
            });
            successInformationMessage();
        } catch {
            renderFailedToCreateError();
        } finally {
            setIsCreatingSubscription(false);
        }
    };

    return (
        <Box data-testid="create-subscription-page" sx={styles.container}>
            <Spinner visible={isVerifying || isCreatingSubscription} sx={styles.spinner} />
            <Box>
                <Typography variant="h6">{t('CREATE_SUBSCRIPTION_PAGE.ENTERPRISE_FORM')}</Typography>
                <Typography variant="body2">{t('CREATE_SUBSCRIPTION_PAGE.SUBTITLE')}</Typography>
            </Box>
            <Box sx={{ marginY: '16px' }}>
                <Box sx={{ alignContent: 'center', display: 'flex', height: 56 }}>
                    <FilledTextField
                        sx={{ width: '400px', marginRight: '16px' }}
                        data-testid="license-id"
                        label={t('CREATE_SUBSCRIPTION_PAGE.LICENSE_ID')}
                        onChange={(event): void => {
                            setLicenseId(event.target.value);
                            setEntitlementError(null);
                            setEntitlement(undefined);
                        }}
                    />
                    <Button
                        data-testid="verify-button"
                        variant="contained"
                        onClick={(): void => {
                            setEntitlementError(null);
                            setEntitlement(undefined);
                            void verifySubscriptionId();
                        }}
                        disabled={!licenseId || licenseId.trim() === '' || isVerifying}
                        sx={{
                            width: '45px',
                            height: 36,
                            borderRadius: '4px',
                            marginTop: '8px',
                            marginRight: '16px',
                        }}
                    >
                        {t('CREATE_SUBSCRIPTION_PAGE.VERIFY')}
                    </Button>

                    {entitlement && (
                        <CheckCircleOutlineIcon
                            data-testid="verified"
                            sx={{ width: 38, height: 38, color: Colors.green[100], marginTop: '8px' }}
                        />
                    )}
                    {entitlement === undefined && entitlementError !== null && (
                        <CancelOutlinedIcon
                            data-testid="not-verified"
                            sx={{ width: 38, height: 38, color: Colors.red[100], marginTop: '8px' }}
                        />
                    )}
                </Box>

                {entitlementError && (
                    <Typography
                        data-testid="error-message"
                        variant="body2"
                        sx={{ color: Colors.red[100], marginTop: '8px' }}
                    >
                        {entitlementError}
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FilledTextField
                    multiline
                    data-testid="license-details"
                    value={entitlement ? `Data: \n ${JSON.stringify(entitlement, null, 2)}` : ''}
                    sx={{
                        height: entitlement ? 160 : 0,
                        width: '100%',
                        marginBottom: entitlement ? '16px' : 0,
                        marginTop: 0,
                        visibility: entitlement ? 'visible' : 'hidden',
                    }}
                    maxRows={10}
                />
                <Box sx={{ height: entitlement ? '100px' : '0px' }} />
                <Autocomplete
                    data-testid="apply-license"
                    options={organizations}
                    getOptionLabel={(option): string => `${option.organizationName} (${option.organizationId})`}
                    renderInput={(params): JSX.Element => (
                        <TextField
                            data-testid="organization-input"
                            {...params}
                            label={t('CREATE_SUBSCRIPTION_PAGE.APPLY_LICENSE_TO_ORGANIZATION')}
                        />
                    )}
                    onChange={(_: any, value: GetOrganizationsForAdmin | null): void => {
                        setSelectedOrganization(value?.organizationId ?? '');
                    }}
                    sx={{ width: '400px', marginBottom: '16px' }}
                />
                <Button
                    data-testid="submit-button"
                    variant="contained"
                    onClick={(): void => {
                        void createSubscription(selectedOrganization, licenseId);
                    }}
                    disabled={
                        !selectedOrganization ||
                        !licenseId ||
                        licenseId.trim() === '' ||
                        entitlement === undefined ||
                        entitlement === null ||
                        isCreatingSubscription
                    }
                    sx={{ width: '77px' }}
                >
                    {t('COMMON.SUBMIT')}
                </Button>
            </Box>
        </Box>
    );
};
